import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";
import { SYS4100_R01Dto } from "../Dto_SYS4100_R01";
import { createSearchFunctionFromCategory } from "src/utils/categoryUtils";

interface UseSYS4100_R01CellChangeProps {
	updateSYS4100_R01List: (index: number, data: SYS4100_R01Dto) => void;
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFocusId: string,
		originalData: SYS4100_R01Dto,
		valueIndex: number,
	) => void;
}

export function useSYS4100_R01CellChange({
	updateSYS4100_R01List,
	settingCodeSearchModalProps,
}: UseSYS4100_R01CellChangeProps) {
	const handleOrgAdminSearch = useCallback(
		async (newValue: string, rowData: SYS4100_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updateSYS4100_R01List(rowData.valueIndex!, {
					...rowData,
					orgAdmin: "",
					orgAdminNm: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("employee");
			if (!searchFn) {
				settingCodeSearchModalProps("부서장", newValue, false, "orgAdmin", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updateSYS4100_R01List(rowData.valueIndex!, {
						...rowData,
						orgAdmin: item.empNo ?? "",
						orgAdminNm: item.empNm ?? "",
						isModified: rowData.inserted ? false : true,
					});
				});
				if (!found) {
					settingCodeSearchModalProps("부서장", newValue, false, "orgAdmin", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("부서장", newValue, false, "orgAdmin", rowData, rowData.valueIndex!);
			}
		}, [updateSYS4100_R01List, settingCodeSearchModalProps]);

	const handleUpOrgSearch = useCallback(
		async (newValue: string, rowData: SYS4100_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updateSYS4100_R01List(rowData.valueIndex!, {
					...rowData,
					upOrgCd: "",
					upOrgNm: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("organization");
			if (!searchFn) {
				settingCodeSearchModalProps("상위부서", newValue, false, "upOrgCd", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updateSYS4100_R01List(rowData.valueIndex!, {
						...rowData,
						upOrgCd: item.orgCd ?? "",
						upOrgNm: item.orgNm ?? "",
						isModified: rowData.inserted ? false : true,
					});
				});
				if (!found) {
					settingCodeSearchModalProps("상위부서", newValue, false, "upOrgCd", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("상위부서", newValue, false, "upOrgCd", rowData, rowData.valueIndex!);
			}
		}, [updateSYS4100_R01List, settingCodeSearchModalProps]);

	const handleMergeOrgSearch = useCallback(
		async (newValue: string, rowData: SYS4100_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updateSYS4100_R01List(rowData.valueIndex!, {
					...rowData,
					mergeOrgCd: "",
					mergeOrgNm: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("organization");
			if (!searchFn) {
				settingCodeSearchModalProps("통합팀", newValue, false, "mergeOrgCd", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updateSYS4100_R01List(rowData.valueIndex!, {
						...rowData,
						mergeOrgCd: item.orgCd ?? "",
						mergeOrgNm: item.orgNm ?? "",
						isModified: rowData.inserted ? false : true,
					});
				});
				if (!found) {
					settingCodeSearchModalProps("통합팀", newValue, false, "mergeOrgCd", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("통합팀", newValue, false, "mergeOrgCd", rowData, rowData.valueIndex!);
			}
		}, [updateSYS4100_R01List, settingCodeSearchModalProps]);

	const handleCellChange = useCallback(
		async (event: CellValueChangedEvent<SYS4100_R01Dto>) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof SYS4100_R01Dto;

			if ((field === "orgAdmin" || field === "orgAdminNm") && newValue !== oldValue) {
				await handleOrgAdminSearch(newValue, data);
				return;
			}

			if ((field === "upOrgCd" || field === "upOrgNm") && newValue !== oldValue) {
				await handleUpOrgSearch(newValue, data);
				return;
			}

			if ((field === "mergeOrgCd" || field === "mergeOrgNm") && newValue !== oldValue) {
				await handleMergeOrgSearch(newValue, data);
				return;
			}

			updateSYS4100_R01List(data.valueIndex!, {
				...data,
				[field]: newValue,
				isModified: true,
			});
		}, [handleOrgAdminSearch, handleUpOrgSearch, handleMergeOrgSearch, updateSYS4100_R01List]);

	return {
		handleCellChange,
	};
}
